import { v4 as uuidv4 } from "uuid";
import { WorksheetCostCategory } from "generated/graphql";
import {
  WorksheetCoverageUnit,
  WorksheetDensityUnit,
  WorksheetHeaderUnit,
  WorksheetLabourUnit,
  WorksheetMaterialCalcMethod,
  WorksheetMaterialOutputUnit,
  WorksheetMaterialUnit,
  WorksheetPricingMode,
  WorksheetThicknessUnit,
} from "generated/graphql";
import { WorksheetCostRowInput, WorksheetItemInput } from "DDD/action-objects/WorksheetItemUpdate";
import { LABOUR_UNIT_OPTIONS, MATERIAL_UNIT_OPTIONS } from "./worksheetOptions";

export const newRowUuid = (): string => uuidv4();

export const getBlankWorksheetHelpers = (): WorksheetItemInput["helpers"] => ({
  calculationVersion: null,
  hoursPerDay: "",
  production: {
    enabled: false,
    source: "item",
    materialRowUuid: "",
    quantity: "",
    unit: "",
    rate: "",
    period: "hour",
  },
  travel: {
    enabled: false,
    roundTripHours: "",
    visits: "",
  },
});

export const getBlankWorksheetCostRow = (
  category: WorksheetCostCategory
): WorksheetCostRowInput => ({
  category,
  name: "",
  sourceType: "",
  sourceId: "",
  sourceName: "",
  sourcePlantId: "",
  sourcePlantName: "",
  quantity: "",
  unit:
    category === WorksheetCostCategory.MATERIAL
      ? WorksheetMaterialUnit.EACH
      : category === WorksheetCostCategory.TRUCKING
      ? ""
      : category === WorksheetCostCategory.LABOR
      ? WorksheetLabourUnit.HOURS
      : "",
  rate: "",
  trucksNeeded: "",
  hoursPerTruck: "",
  truckingUnit: category === WorksheetCostCategory.TRUCKING ? "Hours" : "",
  truckCapacity: "",
  rowUuid: newRowUuid(),
  quantitySource: "manual",
  haulMaterialRowUuid: "",
  haulAllocationPercent: "",
  haulCapacityUnit: "Tons",
  haulRoundTripHours: "",
  haulLoadHours: "",
  haulUnloadHours: "",
  haulPlanningHours: "",
  haulMaxTripsPerDay: "",
  haulQuantityMode: "",
  haulQuantity: "",
  haulUnit: "Tons",
  haulFullLoads: false,
  truckRateHours: "",
  truckRateTons: "",
  truckRateYards: "",
  truckRateLoads: "",
  calcMethod: WorksheetMaterialCalcMethod.MANUAL,
  jobSizeOverride: "",
  jobSizeUnit: "",
  coverageRate: "",
  coverageUnit: WorksheetCoverageUnit.SQUARE_FOOT,
  thickness: "",
  thicknessUnit: WorksheetThicknessUnit.INCH,
  density: "",
  densityUnit: WorksheetDensityUnit.LB_PER_CUFT,
  weightFactor: "",
  outputUnit: WorksheetMaterialOutputUnit.TON,
  quantityOverridden: false,
});

export const getBlankWorksheetItem = (): WorksheetItemInput => ({
  name: "New Item",
  statementOfWork: "",
  unit: WorksheetHeaderUnit.EACH,
  sellingQuantity: "",
  sellingRate: "",
  fixedTotal: "",
  pricingMode: WorksheetPricingMode.RATE,
  pricingOverrideField: "none",
  pricingBaseline: null,
  markupPercentage: "",
  overheadPercentage: "",
  indirectPercentage: "",
  isOptional: false,
  organizationProductTypeId: "",
  internalName: "",
  discount: "",
  helpers: getBlankWorksheetHelpers(),
  costRows: [],
});

export const getMovedCostRow = (
  row: WorksheetCostRowInput,
  category: WorksheetCostCategory,
  effectiveQuantity: number
): WorksheetCostRowInput => {
  const next: WorksheetCostRowInput = {
    ...getBlankWorksheetCostRow(category),
    name: row.name,
    rate: row.rate,
    rowUuid: row.rowUuid,
  };
  const quantity = String(effectiveQuantity);
  const allowedUnits =
    category === WorksheetCostCategory.MATERIAL ? MATERIAL_UNIT_OPTIONS : LABOUR_UNIT_OPTIONS;

  if (
    category !== WorksheetCostCategory.TRUCKING &&
    allowedUnits.some((option) => option.value === row.unit)
  ) {
    next.unit = row.unit;
  }

  if (category === WorksheetCostCategory.TRUCKING) {
    next.hoursPerTruck = quantity;
  } else {
    next.quantity = quantity;
  }

  return next;
};

export type WorksheetLibrarySelection = {
  sourceType: string;
  sourceId: string;
  sourceName: string;
  sourcePlantId?: string;
  sourcePlantName?: string;
  rate: string;
  unit: string;
  quantity?: string;
  name?: string;
  truckingUnit?: string;
  trucksNeeded?: string;
  truckCapacity?: string;
  truckRateHours?: string;
  truckRateTons?: string;
};

const LIBRARY_SELECTION_FIELDS = [
  "sourceType",
  "sourceId",
  "sourceName",
  "sourcePlantId",
  "sourcePlantName",
  "rate",
  "unit",
  "quantity",
  "name",
  "truckingUnit",
  "trucksNeeded",
  "truckCapacity",
  "truckRateHours",
  "truckRateTons",
] as const;

export const applyLibrarySelectionToRow = (
  row: WorksheetCostRowInput,
  selection: WorksheetLibrarySelection
): WorksheetCostRowInput => {
  const next: Record<string, unknown> = { ...row };

  LIBRARY_SELECTION_FIELDS.forEach((field) => {
    const value = selection[field];

    if (value === undefined || value === null) {
      return;
    }

    if (field === "name" && row.name) {
      return;
    }

    next[field] = value;
  });

  return next as WorksheetCostRowInput;
};

export const librarySelectionToNewRow = (
  category: WorksheetCostCategory,
  selection: WorksheetLibrarySelection
): WorksheetCostRowInput =>
  applyLibrarySelectionToRow(getBlankWorksheetCostRow(category), selection);
